// MP316b — Feature-flag SDK for the customer app.
//
// Reads the resolved-flag set from `/v1/tenants/:orgId/feature-flags`
// once per session on first use, then refreshes every 60s in the
// background. Module-scope cache so every hook consumer shares a
// single network round-trip per org.
//
// Fail-safe model:
//   - Endpoint failure → cache stays empty; every hook returns
//     enabled=false. UI hides guarded surfaces instead of crashing.
//   - Missing / unknown flag key → same treatment: enabled=false.
//     Deleting a flag mid-runtime never breaks a running SPA.
//
// Also exposes tenant Beta opt-in helpers used by the Settings →
// Beta features surface.

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { jsonOrThrow } from './apiError';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8080';
const REFRESH_INTERVAL_MS = 60_000;

export type FeatureFlagState = 'off' | 'beta' | 'on';

export interface ResolvedFlag {
  key: string;
  label: string;
  description: string;
  /** EFFECTIVE state. `on` when the flag carries a scheduledEnableAt
   *  that has passed, even if the stored doc still says off/beta. */
  state: FeatureFlagState;
  appId: string | null;
  enabled: boolean;
  isBeta: boolean;
  /** True when the flag is in Beta state and this org is NOT enabled by
   *  either the beta-org list or the percentage ramp. Drives the "opt in
   *  to Beta" surface in Settings. */
  availableInBeta: boolean;
  /** MP316d — this org is enabled by the platform's percentage ramp
   *  rather than by an explicit opt-in. Absent on an api that predates
   *  the rollout controls. */
  viaRollout?: boolean;
  /** MP316d — the ramp configured on the flag (0 = none). */
  rolloutPercent?: number;
  /** MP316d — pending scheduled-enable moment (epoch ms), or null. */
  scheduledEnableAt?: number | null;
  /** MP316g — when the server disabled this flag because its app-level
   *  flag (`app.<slug>`) is off, this names that app key. Null when the
   *  flag's own state decided. Absent on an api that predates MP316g. */
  disabledByApp?: string | null;
}

interface CacheEntry {
  orgId: string;
  ts: number;
  resolved: Record<string, ResolvedFlag>;
}

// Module-scope cache keyed by orgId. Multiple consumers share one
// fetch — useFeatureFlag + useAllFeatureFlags on the same page never
// double-fetch.
const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<CacheEntry | null>>();
const subscribers = new Set<() => void>();

// Last resolved set per org, persisted in localStorage so a page load
// renders the SAME nav the previous load settled on, instead of the
// fail-open guess. Without this, every load of a tenant not opted into
// a `beta` flag showed the gated item for ~300ms and then removed it
// (the Surface "Unified inventory" flicker, 2026-09-13). The persisted
// entry is a starting point only: `ensureFresh` still fetches on every
// mount and the 60s poll keeps it current, so an opt-in/out lands in
// the next fetch as before. Per-viewer convenience — a missing or
// unreadable store just means one first-visit flash.
const PERSIST_PREFIX = 'xone.featureFlags.v1.';

function loadPersisted(orgId: string): CacheEntry | null {
  try {
    const raw = localStorage.getItem(PERSIST_PREFIX + orgId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CacheEntry>;
    if (parsed.orgId !== orgId || !parsed.resolved || typeof parsed.resolved !== 'object') return null;
    return { orgId, ts: typeof parsed.ts === 'number' ? parsed.ts : 0, resolved: parsed.resolved };
  } catch {
    return null;
  }
}

function savePersisted(entry: CacheEntry): void {
  try {
    localStorage.setItem(PERSIST_PREFIX + entry.orgId, JSON.stringify(entry));
  } catch {
    /* quota / private mode — the in-memory cache still works */
  }
}

function notify() {
  for (const fn of subscribers) fn();
}

/** Invalidate the cached resolved set for an org. Call after a Beta
 *  opt-in/opt-out so the surface flips immediately instead of waiting
 *  for the 60s poll. */
export function invalidateFeatureFlags(orgId: string) {
  cache.delete(orgId);
  notify();
}

async function fetchResolved(
  idToken: string,
  orgId: string,
): Promise<CacheEntry | null> {
  try {
    const res = await fetch(
      `${API_URL}/v1/tenants/${encodeURIComponent(orgId)}/feature-flags`,
      { headers: { authorization: `Bearer ${idToken}` } },
    );
    if (!res.ok) {
      // Any non-2xx is a fail-safe: don't cache the failure, just
      // return null. Consumers stay in "enabled=false for everything"
      // state until the next successful refresh.
      // eslint-disable-next-line no-console
      console.warn('[featureFlags] fetch failed', { status: res.status, orgId });
      return null;
    }
    const body = (await res.json().catch(() => ({}))) as {
      resolved?: Record<string, ResolvedFlag>;
    };
    return {
      orgId,
      ts: Date.now(),
      resolved: body.resolved ?? {},
    };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[featureFlags] fetch threw', {
      err: err instanceof Error ? err.message : String(err),
      orgId,
    });
    return null;
  }
}

/** Kick off (or reuse) an in-flight fetch. Prevents thundering-herd
 *  when many hooks mount at the same tick. */
async function ensureFresh(idToken: string, orgId: string): Promise<void> {
  if (inflight.has(orgId)) {
    await inflight.get(orgId);
    return;
  }
  const p = fetchResolved(idToken, orgId).then((entry) => {
    if (entry) {
      cache.set(orgId, entry);
      savePersisted(entry);
      notify();
    }
    inflight.delete(orgId);
    return entry;
  });
  inflight.set(orgId, p);
  await p;
}

// ---------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------

/** All resolved flags for the active org. Callers that only need one
 *  flag should prefer useFeatureFlag('...') — same cache, simpler
 *  return shape. */
export function useAllFeatureFlags(): {
  flags: ResolvedFlag[];
  loading: boolean;
  refetch: () => Promise<void>;
} {
  const { session } = useAuth();
  const orgId = session?.activeOrgId ?? null;
  const [, setTick] = useState(0);

  useEffect(() => {
    const fn = () => setTick((n) => n + 1);
    subscribers.add(fn);
    return () => {
      subscribers.delete(fn);
    };
  }, []);

  // Initial fetch + polling loop. Both hang off the auth session, so
  // sign-out cancels naturally.
  useEffect(() => {
    if (!session || !orgId) return;
    let cancelled = false;
    let interval: ReturnType<typeof setInterval> | null = null;
    void (async () => {
      const idToken = await session.user.getIdToken();
      if (cancelled) return;
      await ensureFresh(idToken, orgId);
      // Refresh every 60s. Uses a fresh id token each cycle so the
      // hook keeps working across the Firebase token 1h rotation.
      interval = setInterval(async () => {
        try {
          const t = await session.user.getIdToken();
          await ensureFresh(t, orgId);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[featureFlags] refresh failed', err);
        }
      }, REFRESH_INTERVAL_MS);
    })();
    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
    };
  }, [session, orgId]);

  // Cold start: seed the module cache from the persisted set so the
  // first render matches the last settled state. `ensureFresh` (above)
  // is already in flight and will overwrite it.
  let entry = orgId ? cache.get(orgId) : undefined;
  if (!entry && orgId) {
    const persisted = loadPersisted(orgId);
    if (persisted) {
      cache.set(orgId, persisted);
      entry = persisted;
    }
  }

  const refetch = useCallback(async () => {
    if (!session || !orgId) return;
    const idToken = await session.user.getIdToken();
    // Drop the cached entry so ensureFresh treats this as a cold fetch.
    cache.delete(orgId);
    await ensureFresh(idToken, orgId);
  }, [session, orgId]);

  return {
    flags: entry ? Object.values(entry.resolved) : [],
    loading: !entry && !!orgId,
    refetch,
  };
}

/** Single-key hook.
 *
 *  MP316i — aligned with `FeatureFlagRoute`, the nav filter, the
 *  launcher and the server gate (#939): a key that is NOT in the
 *  resolved set passes OPEN. A doc nobody has created is not a switch
 *  somebody turned off; only a present-and-disabled flag hides a
 *  surface. While the set is still loading the answer is also "open",
 *  so a legitimately-enabled affordance never flashes out and back.
 *
 *  Pre-MP316i this hook failed closed on a missing key — the one
 *  consumer-facing API in the flag system that disagreed with the
 *  others. It had no call sites at the time of the change. */
export function useFeatureFlag(key: string): {
  enabled: boolean;
  isBeta: boolean;
  availableInBeta: boolean;
  loading: boolean;
} {
  const { flags, loading } = useAllFeatureFlags();
  const found = flags.find((f) => f.key === key);
  if (!found) {
    return { enabled: true, isBeta: false, availableInBeta: false, loading };
  }
  return {
    enabled: found.enabled,
    isBeta: found.isBeta,
    availableInBeta: found.availableInBeta,
    loading,
  };
}

// ---------------------------------------------------------------------
// Tenant Beta opt-in helpers
// ---------------------------------------------------------------------

export async function optInToBetaFlag(
  idToken: string,
  orgId: string,
  key: string,
): Promise<ResolvedFlag> {
  const res = await fetch(
    `${API_URL}/v1/tenants/${encodeURIComponent(orgId)}/feature-flags/${encodeURIComponent(key)}/beta-optin`,
    {
      method: 'POST',
      headers: { authorization: `Bearer ${idToken}` },
    },
  );
  const body = (await jsonOrThrow(res)) as { flag: ResolvedFlag };
  invalidateFeatureFlags(orgId);
  return body.flag;
}

export async function optOutOfBetaFlag(
  idToken: string,
  orgId: string,
  key: string,
): Promise<ResolvedFlag> {
  const res = await fetch(
    `${API_URL}/v1/tenants/${encodeURIComponent(orgId)}/feature-flags/${encodeURIComponent(key)}/beta-optin`,
    {
      method: 'DELETE',
      headers: { authorization: `Bearer ${idToken}` },
    },
  );
  const body = (await jsonOrThrow(res)) as { flag: ResolvedFlag };
  invalidateFeatureFlags(orgId);
  return body.flag;
}
